import { useEffect, useState } from "react";
import clsx from "clsx";
import useObservable from "@core/hooks/use-observable.hook";
import PcComponentService from "@app/services/http/pc.component.service";
import RentalRequestService from "@app/services/http/rental.request.service";
import AccountService from "@app/services/http/account.service";
import { openPortalDialog } from "@app/services/modal.service";
import RentalRequestModal from "@app/components/rental-request-modal";
import { Account, IRentalRequestPcComponent, PcComponent } from "@app/types";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES, MAX_LEN } from "@app/constants";
import { useT } from "@app/contexts/preferences.context";
import { IcBell, IcBookOpen, IcCart, IcFileText, IcGrid, IcMapPin, IcSearch, IcShield } from "@app/components/icons";
import { addToast } from "@app/components/toast/toast.service";
import { btn, btnGhost, btnPrimary, btnSm, select } from "@app/components/ui";
import { PART_CATEGORIES, PartCategory, categoryOf, partArtBackground, partIconShadow, specList } from "./pc-parts";
import PcInfoModal, { PcInfoKind } from "./pc-info-modal";
import PcRequests from "./pc-requests";

type Sort = "stock" | "lo" | "hi";

const ALL = "all";

/** Số yêu cầu thuê tối đa mỗi tài khoản giữ cùng lúc, giống API (MAX_RENTAL_REQUESTS) */
const MAX_RENTAL_REQUESTS = 10;

/** Bỏ dấu tiếng Việt để tìm kiếm */
const fold = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase();

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

/** Trang Thuê linh kiện mang về: danh sách linh kiện (PcComponentService như cũ) + Yêu cầu đã gửi. */
export default function PcComponentClientPage() {
  const t = useT();
  const [components, setComponents] = useState<PcComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<Account>();

  const [category, setCategory] = useState(ALL);
  const [q, setQ] = useState("");
  const [onlyStock, setOnlyStock] = useState(false);
  const [sort, setSort] = useState<Sort>("stock");

  const [requests, setRequests] = useState<IRentalRequestPcComponent[]>([]);
  const [reqPage, setReqPage] = useState(DEFAULT_PAGE);
  const [reqTotalPages, setReqTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [reqCount, setReqCount] = useState(0);
  const [reqUpdate, setReqUpdate] = useState(0);
  const [reqLoaded, setReqLoaded] = useState(false);
  const [notifyIds, setNotifyIds] = useState<string[]>([]);

  const { subscribeOnce, subscribeUntilDestroy } = useObservable();

  useEffect(() => {
    setLoading(true);
    subscribeUntilDestroy(PcComponentService.getPcComponents(), (res: PcComponent[]) => {
      setComponents(res || []);
      setLoading(false);
    });
    subscribeOnce(AccountService.getMyInfo(), (data) => data && setAccount(data));
    subscribeOnce(PcComponentService.getMyNotify(), (ids) => setNotifyIds((ids || []).map(String)));
  }, []);

  /** "Báo khi có hàng": bật / tắt, server gửi email khi admin chuyển linh kiện sang còn hàng */
  const toggleNotify = (item: PcComponent) => {
    subscribeOnce(PcComponentService.toggleNotify(item._id), (res) => {
      setNotifyIds((ids) => (res?.subscribed ? [...ids, item._id] : ids.filter((id) => id !== item._id)));
      addToast({
        text: res?.subscribed
          ? `${t("Sẽ báo qua email khi có hàng")}: ${item.name}`
          : `${t("Đã tắt báo có hàng")}: ${item.name}`,
      });
    });
  };

  useEffect(() => {
    subscribeUntilDestroy(RentalRequestService.getMyRentalRequests(reqPage), (res) => {
      setRequests(res?.data || []);
      setReqLoaded(true);
      setReqTotalPages(res?.total_page || DEFAULT_TOTAL_PAGES);
      setReqCount(res?.count || 0);
    });
  }, [reqPage, reqUpdate]);

  const openInfo = (kind: PcInfoKind) => openPortalDialog(PcInfoModal, { kind });

  const openRequest = (component: PcComponent) => {
    if (reqLoaded && reqCount >= MAX_RENTAL_REQUESTS) {
      // admin có thể vừa xoá bớt yêu cầu: hỏi lại số yêu cầu trước khi chặn
      subscribeOnce(RentalRequestService.getMyRentalRequests(DEFAULT_PAGE), (res) => {
        const count = res?.count || 0;

        setReqCount(count);

        if (count >= MAX_RENTAL_REQUESTS) {
          addToast({
            text: `${t("Bạn đã có")} ${count} ${t("yêu cầu thuê (tối đa")} ${MAX_RENTAL_REQUESTS}). ${t(
              "Hãy chờ PCRENDER xử lý hoặc liên hệ hỗ trợ."
            )}`,
            status: "warn",
          });
        } else {
          openRequestModal(component);
        }
      });

      return;
    }

    openRequestModal(component);
  };

  const openRequestModal = (component: PcComponent) => {
    openPortalDialog(RentalRequestModal, { component, account, onTerms: () => openInfo("terms") })
      .afterClosed()
      .subscribe((res) => {
        if (res?.sent) {
          setReqPage(DEFAULT_PAGE);
          setReqUpdate((n) => n + 1);
        }

        if (res?.see) {
          setTimeout(() => scrollTo("pc-requests"), 100);
        }
      });
  };

  const inCategory = (item: PcComponent, cat: string) =>
    cat === ALL || PART_CATEGORIES.find((c) => c.name === cat)?.values.includes(item.category);

  const query = fold(q.trim());
  const rows = components
    .filter(
      (item) =>
        inCategory(item, category) &&
        (!onlyStock || item.in_stock) &&
        (!query ||
          fold([item.name, item.description, item.category, ...specList(item).map(([, v]) => v)].join(" ")).includes(query))
    )
    .sort((a, b) =>
      sort === "lo"
        ? a.daily_rental_rate - b.daily_rental_rate
        : sort === "hi"
        ? b.daily_rental_rate - a.daily_rental_rate
        : (b.in_stock ? 1 : 0) - (a.in_stock ? 1 : 0)
    );

  const chip = (key: string, name: string, sub: string, Icon: PartCategory["icon"], color?: string) => {
    const on = category === key;

    return (
      <button
        key={key}
        type="button"
        aria-pressed={on}
        onClick={() => setCategory(key)}
        className={clsx(
          "flex flex-none items-center gap-2.5 rounded-xl border py-2 pl-2 pr-3.5 text-left",
          on ? "border-brand bg-brand text-white" : "border-line bg-surface text-fg hover:border-brand-line"
        )}
      >
        <span
          className="grid h-8 w-8 flex-none place-items-center rounded-[9px]"
          style={
            on
              ? { background: "rgba(255,255,255,.18)", color: "#fff" }
              : { background: `color-mix(in srgb, ${color || "var(--brand)"} 14%, transparent)`, color: color || "var(--brand)" }
          }
        >
          <Icon className="h-[17px] w-[17px]" />
        </span>
        <span>
          <b className="block whitespace-nowrap text-[13px]">{t(name)}</b>
          <small className={clsx("whitespace-nowrap text-[11px]", on ? "text-white/80" : "text-faint")}>{sub}</small>
        </span>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-5 px-6 pb-12 pt-6 max-[1100px]:pb-24 max-[900px]:px-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-bold tracking-[-.015em] max-[760px]:text-[22px]">{t("Thuê linh kiện mang về")}</h1>
          <p className="m-0 mt-1 max-w-[62ch] text-muted">
            {t("Thuê CPU, VGA, RAM, SSD… về lắp vào máy của bạn. PCRENDER giao và nhận lại tận nơi.")}
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className={clsx(btn, btnGhost, btnSm)} onClick={() => openInfo("how")}>
            <IcBookOpen className="h-4 w-4" />
            {t("Cách thức thuê")}
          </button>
          <button type="button" className={clsx(btn, btnGhost, btnSm)} onClick={() => openInfo("terms")}>
            <IcFileText className="h-4 w-4" />
            {t("Điều khoản thuê")}
          </button>
        </div>
      </div>

      <div className="no-scrollbar flex gap-2 overflow-x-auto p-0.5" role="group" aria-label={t("Loại linh kiện")}>
        {chip(ALL, "Tất cả", `${components.length} ${t("sản phẩm")}`, IcGrid)}
        {PART_CATEGORIES.map((c) => chip(c.name, c.name, t(c.sub), c.icon, c.color))}
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <label className="flex h-10 min-w-[220px] flex-1 items-center gap-2 rounded-[10px] max-[760px]:min-w-full border border-line bg-surface px-3 text-muted focus-within:border-brand">
          <IcSearch className="h-[18px] w-[18px] flex-none" />
          <input
            type="search"
            maxLength={MAX_LEN.SEARCH}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Tìm linh kiện, ví dụ: 4090, DDR5, Z790…")}
            className="min-w-0 flex-1 border-0 bg-transparent text-fg outline-none"
          />
        </label>
        <label className="flex items-center gap-1.5 whitespace-nowrap text-[13px] text-muted">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--brand)]"
            checked={onlyStock}
            onChange={(e) => setOnlyStock(e.target.checked)}
          />
          {t("Chỉ hiện còn hàng")}
        </label>
        <select className={clsx(select, "!h-10")} value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label={t("Sắp xếp")}>
          <option value="stock">{t("Còn hàng trước")}</option>
          <option value="lo">{t("Giá thấp đến cao")}</option>
          <option value="hi">{t("Giá cao đến thấp")}</option>
        </select>
      </div>

      <div className="flex items-baseline justify-between">
        <h2 className="text-[17px] font-bold">{t("Linh kiện cho thuê")}</h2>
        <span className="text-[13px] text-muted">
          {rows.length} {t("sản phẩm")}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-brand" />
        </div>
      ) : !rows.length ? (
        <div className="rounded-card border border-dashed border-line-2 px-4 py-10 text-center">
          <b>{t("Không có linh kiện phù hợp")}</b>
          <p className="m-0 mt-1 text-muted">{t("Thử từ khoá khác, chọn loại khác hoặc bỏ lọc \"Chỉ hiện còn hàng\".")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-3.5 max-[760px]:grid-cols-[repeat(auto-fill,minmax(220px,1fr))] max-[480px]:grid-cols-1">
          {rows.map((item) => {
            const cat = categoryOf(item.category);
            const specs = specList(item);

            return (
              <article
                key={item._id}
                className={clsx(
                  "flex flex-col overflow-hidden rounded-[14px] border border-line bg-surface transition-shadow hover:border-brand-line hover:shadow-card",
                  !item.in_stock && "opacity-80"
                )}
              >
                {/* không dùng ảnh: icon + gradient theo màu danh mục như bản thiết kế */}
                <div
                  className="relative grid h-[118px] flex-none place-items-center overflow-hidden"
                  style={{ background: partArtBackground(cat.color), color: cat.color }}
                >
                  <span
                    className="grid h-[62px] w-[62px] place-items-center rounded-[18px] bg-surface"
                    style={{ boxShadow: partIconShadow(cat.color) }}
                  >
                    <cat.icon className="h-8 w-8" />
                  </span>
                  <span className="absolute left-2.5 top-2.5 rounded-full bg-surface px-2 py-0.5 text-[11px] font-bold" style={{ color: cat.color }}>
                    {cat.name}
                  </span>
                  {!item.in_stock && (
                    <span className="absolute right-2.5 top-2.5 rounded-full bg-bad-soft px-2 py-0.5 text-[11px] font-bold text-bad">
                      {t("Hết hàng")}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-2 px-3.5 pb-3.5 pt-3">
                  <h3 className="text-[15px] font-bold leading-tight">{item.name}</h3>
                  {item.description && <p className="m-0 -mt-1 line-clamp-2 text-[12.5px] text-muted">{item.description}</p>}
                  <div className="grid grid-cols-2 gap-1.5 tabular-nums">
                    {(
                      [
                        [item.daily_rental_rate, "ngày"],
                        [item.monthly_rental_rate, "tháng"],
                      ] as [number, string][]
                    ).map(([price, unit]) => (
                      <div key={unit} className="flex flex-col rounded-[10px] bg-brand-soft px-2.5 py-1.5">
                        <b className="text-[15px] text-brand">{(price || 0).toLocaleString("vi-VN")}đ</b>
                        <span className="text-[11px] text-muted">/{t(unit)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="m-0 flex items-center gap-1.5 rounded-lg bg-warn-soft px-2 py-1 text-xs text-warn">
                    <IcShield className="h-3.5 w-3.5 flex-none" />
                    <span>
                      {t("Cọc")} <b className="tabular-nums">{(item.deposit || 0).toLocaleString("vi-VN")}đ</b> · {t("hoàn lại khi trả")}
                    </span>
                  </p>
                  {!!specs.length && (
                    <ul className="m-0 flex list-disc flex-col gap-0.5 pl-4 text-[12.5px] text-muted">
                      {specs.map(([label, value]) => (
                        <li key={label}>
                          {t(label)}: <b className="font-semibold text-fg">{value}</b>
                        </li>
                      ))}
                    </ul>
                  )}
                  {item.location && (
                    <p className="m-0 flex items-center gap-1.5 text-xs text-muted">
                      <IcMapPin className="h-3.5 w-3.5 flex-none" />
                      {t("Khu vực hỗ trợ")}: {item.location}
                    </p>
                  )}
                  <div className="mt-auto flex items-center justify-between gap-2 pt-1">
                    <span
                      className={clsx(
                        "inline-flex items-center gap-1.5 text-xs font-semibold before:h-[7px] before:w-[7px] before:rounded-full before:bg-current",
                        item.in_stock ? "text-ok" : "text-bad"
                      )}
                    >
                      {item.in_stock ? t("Còn hàng") : t("Hết hàng")}
                    </span>
                    {item.in_stock ? (
                      <button type="button" className={clsx(btn, btnSm, btnPrimary)} onClick={() => openRequest(item)}>
                        <IcCart className="h-4 w-4" />
                        {t("Thuê")}
                      </button>
                    ) : (
                      <button
                        type="button"
                        aria-pressed={notifyIds.includes(item._id)}
                        className={clsx(btn, btnSm, btnGhost, notifyIds.includes(item._id) && "!border-brand !text-brand")}
                        onClick={() => toggleNotify(item)}
                      >
                        <IcBell className="h-4 w-4" />
                        {notifyIds.includes(item._id) ? t("Đã đăng ký báo") : t("Báo khi có hàng")}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <PcRequests
        loading={!reqLoaded}
        requests={requests}
        count={reqCount}
        page={reqPage}
        totalPages={reqTotalPages}
        onPageChange={setReqPage}
        onAgain={openRequest}
      />
    </div>
  );
}
